/**
 * iframe 嵌入模式下的 postMessage 桥接
 * 只在 isShellEmbedded() === true 时激活。
 */
import Router from 'vue-router';
import pathToRegexp from 'path-to-regexp';
import { isShellEmbedded } from '@/libs/util';

interface MessageEnvelope {
  v: number;
  type: string;
  source: 'shell' | 'iframe';
  payload?: any;
}

interface Registry {
  nativePatterns: string[];
  pathRewrites: Record<string, string>;
}

interface CompiledRegistry {
  matchers: Array<{ regexp: RegExp; original: string }>;
  pathRewrites: Record<string, string>;
}

/**
 * 旧版 iframe 触发新版壳展示套餐/功能限制弹窗的业务场景。
 * 这里只传递场景枚举，具体文案、按钮行为、升级/续费类型由 salesmartly-fe 统一决定。
 */
export type PackageGuideScene =
  /** 通用功能套餐限制，适用于没有更细分业务场景的升级提示。 */
  | 'featureLimit'
  /** Max版功能受当前套餐限制。 */
  | 'maxLimit'
  /** 套餐有效期已结束，需要引导用户续费或联系商务演示。 */
  | 'packageExpired'
  /** 社媒账号、渠道启用数量达到当前套餐上限。 */
  | 'channelLimit'
  /** 团队成员数量达到当前套餐上限。 */
  | 'memberLimit'
  /** 云托管设备数量达到当前套餐上限。 */
  | 'cloudDeviceLimit'
  /** 大报表、数据分析相关功能受当前套餐限制。 */
  | 'dataAnalysisLimit'
  /** 分流链接功能受当前套餐限制。 */
  | 'splitLinkLimit';

/**
 * iframe:package-guide-prompt 消息体。
 * 旧版页面不再向新版传递文案、跳转类型或来源路径，避免新旧两套展示逻辑耦合。
 */
export interface PackageGuidePromptPayload {
    scene: PackageGuideScene;
}

export interface SwitchNewUiPayload {
    isNewUi: '2' | '3';
}

export type AiRobotUpgradeGuideLaterAction = 'createRobot';

export interface AiRobotUpgradeGuidePayload {
    /** 用户点击“稍后再说”后由 shell 执行的后续动作 */
    laterAction?: AiRobotUpgradeGuideLaterAction;
}

let compiled: CompiledRegistry = { matchers: [], pathRewrites: {} };

/**
 * 标记当前是否正在处理 shell 主动发起的 router.replace。
 * shell:navigate 是 shell 已决策的显式命令（包括 iframeForceShow 场景），
 * 不应被 iframe 侧的迁移判定 beforeEach 拦截。
 */
let shellDrivenNavigation = false;

/**
 * 当前正在处理的 shell:navigate-guard 请求 id。
 * 任意时刻最多一个 inflight；新请求到达时旧请求立刻回 allow=false。
 */
let inflightGuard: { id: string } | null = null;

/**
 * tryGuardedNavigate 的串行化 Promise 链。
 * 新 guard 请求排队等上一个完全 settle，避免 Modal 堆叠。
 */
let guardChain: Promise<void> = Promise.resolve();

/**
 * 同一批次内是否已经有 step 让用户取消过。
 *
 * 用户在快速连点 A→B→C 场景下，当 step-A 真触发 beforeRouteLeave 弹出 Modal A
 * 而用户点 Cancel 时，已经表达了"留在当前 dirty 页"的意愿。本批次 chain 队列中
 * 后续仍 inflight 命中的 step（典型：step-C）不应再次调用 router.push 触发
 * 第二次 Modal —— 否则会出现"Modal 闪烁"且可能让 iView Modal 子系统状态错乱
 * 导致后续的 Modal.confirm 静默失效。
 *
 * 一个新"批次"在 inflightGuard 从 null 转为非 null 时开启（由 case handler 复位）。
 */
let guardCancelled = false;

/**
 * 仅供单元测试重置 inflight/chain 状态。
 *
 * 注意：此函数不重置 activeMessageHandler。每个测试需要重新调用
 * initEmbeddedBridge(router) 才能注册新 router 的 handler。
 */
export function __resetGuardStateForTests(): void {
    inflightGuard = null;
    guardChain = Promise.resolve();
    guardCancelled = false;
}

/**
 * 当前注册的 message 监听器。
 * initEmbeddedBridge 重复调用时先移除旧监听器，避免测试中多实例堆叠。
 */
let activeMessageHandler: ((event: MessageEvent) => void) | null = null;

/** 供 mix_ads_fe router guard 使用：当前导航是否由 shell:navigate 触发 */
export function isShellDrivenNavigation(): boolean {
    return shellDrivenNavigation;
}

export type NavigationDecision =
  | { kind: 'rewrite'; newPath: string }
  | { kind: 'native' }
  | { kind: 'legacy' };

function stripQueryHash(path: string): string {
    const qIdx = path.indexOf('?');
    if (qIdx >= 0) path = path.slice(0, qIdx);
    const hIdx = path.indexOf('#');
    if (hIdx >= 0) path = path.slice(0, hIdx);
    return path;
}

function applyRegistry(reg: Registry): void {
    const matchers: CompiledRegistry['matchers'] = [];
    reg.nativePatterns.forEach((pattern) => {
        try {
            const regexp = pathToRegexp(pattern);
            matchers.push({ regexp, original: pattern });
        } catch (e) {
            console.error('[embedded-bridge] invalid pattern skipped:', pattern, e);
        }
    });
    compiled = { matchers, pathRewrites: { ...reg.pathRewrites } };
}

/** 仅供单元测试直接注入 registry */
export function __setRegistryForTests(reg: Registry): void {
    applyRegistry(reg);
}

/**
 * tryGuardedNavigate 守卫等待用户决策的最大时长（毫秒）。
 *
 * 业务守卫若忘记调用 next() / next(false)（例如历史遗留 bug:
 * AutomationFlow/edit.vue 早期 onCancel 注释了 next 调用），
 * router.push 永远 pending,会让 chain step 卡死、后续 shell 切换全部无响应。
 * 5 分钟兜底:足够覆盖真实用户阅读+决策时间,又能在异常时让系统最终自愈。
 */
const TRY_GUARDED_NAVIGATE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * 在 iframe 内真实派发一次 router.push，用 afterEach 是否触发作为
 * 守卫放行与否的判定信号。
 *
 * - 守卫调用 next() 放行 → vue-router 完成导航 → afterEach 触发 → confirmed=true
 * - 守卫调用 next(false) / 抛错 → push promise 走 catch 分支 → confirmed 保持 false
 * - 守卫调用 next({redirect}) → patched push 把 NavigationRedirected 抑制成 resolve(err) →
 *   .then 内识别后直接 settle(true)，不要求 afterEach 触发。典型场景:`resolveNavigation = 'native'`
 *   分支会把目标改写到 `/idle`,beforeRouteLeave 此时还未运行,但这并非"用户拒绝离开",
 *   shell 端 push 应当继续提交,否则 URL 永远卡在 iframe 页。
 * - 守卫忘记调用 next（业务 bug） → 5 分钟超时强制 resolve(false) 防止系统锁死
 *
 * 时序保证：vue-router 3 在所有 afterEach 钩子执行完毕后才 resolve push promise，
 * 因此 .then() 中读取 confirmed 时 afterEach 回调必然已经运行完成。
 *
 * 资源清理：仅在 push promise settle（resolve 或 reject）时调用 offAfter()。
 * 若调用方传入永不 settle 的 router（生产环境 vue-router 3 不会出现，仅测试 mock 可能），
 * afterEach 监听器会泄漏；本函数不做防御，请保证传入的是真实 vue-router 实例或会 settle 的 mock。
 *
 * 内部 export，仅供同模块的 shell:navigate-guard handler 与单测使用。
 */
export function tryGuardedNavigate(router: Router, targetPath: string): Promise<boolean> {
    return new Promise((resolve) => {
        let confirmed = false;
        let settled = false;
        const settle = (value: boolean) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };
        const offAfter = router.afterEach(() => { confirmed = true; });
        const timer = setTimeout(() => {
            offAfter();
            console.warn('[embedded-bridge] tryGuardedNavigate 5min 超时,业务守卫未调用 next:', targetPath);
            settle(false);
        }, TRY_GUARDED_NAVIGATE_TIMEOUT_MS);
        /**
         * vue-router 3.5.1 仅对 NavigationDuplicated 显式设 `error.name = 'NavigationDuplicated'`,
         * NavigationRedirected/Cancelled/Aborted 的 name 都是默认 'Error'(只有 `_isRouter` 与
         * `type` 字段可靠)。`router/index.ts` 的 patched push 用 `err.name` 匹配抑制清单,因此实际
         * 只能把 NavigationDuplicated 从 reject 改写为 resolve(err);Redirected/Cancelled 仍走 reject。
         *
         * 本函数据此双路径识别:
         * - `.then(result)`: 命中 patched 抑制路径,只有 NavigationDuplicated。
         * - `.catch(err)`: 用 `err._isRouter && err.type` 精确识别 Redirected (=2) / Cancelled (=8)。
         *
         * 三类都不是"用户在 beforeRouteLeave 主动 next(false)"语义,放行 settle(true):
         * - Redirected: 内部 beforeEach 把目标改写到别处(典型 'native' 分支 → `/idle`)。
         *   shell 端 askIframeGuard 等的就是"iframe 没拒绝离开",收到 allow=true 后
         *   自身 push 才能提交,否则 URL 卡在 iframe 页。
         * - Duplicated: 已经在目标路径,trivially 允许。
         * - Cancelled: 被并发新导航取代,从"是否阻止离开"看不算拒绝。
         *
         * 仅 NavigationAborted (next(false), type=4) 与未知异常走 settle(false),
         * 与 5min 超时一起作为真正的"守卫拒绝"信号。
         */
        const NAVIGATION_TYPE_REDIRECTED = 2;
        const NAVIGATION_TYPE_CANCELLED = 8;
        const isVueRouterNavFailure = (err: any, type: number): boolean => {
            return !!err && err._isRouter === true && err.type === type;
        };
        router.push(targetPath)
            .then((result: any) => {
                clearTimeout(timer);
                offAfter();
                if (result && typeof result === 'object' && (result as { name?: unknown }).name === 'NavigationDuplicated') {
                    settle(true);
                    return;
                }
                settle(confirmed);
            })
            .catch((err: any) => {
                clearTimeout(timer);
                offAfter();
                if (
                    isVueRouterNavFailure(err, NAVIGATION_TYPE_REDIRECTED) ||
                    isVueRouterNavFailure(err, NAVIGATION_TYPE_CANCELLED)
                ) {
                    settle(true);
                    return;
                }
                settle(false);
            });
    });
}

/**
 * 判定目标路径归属，供 router.beforeEach 使用
 */
export function resolveNavigation(path: string): NavigationDecision {
    const pathname = stripQueryHash(path);
    if (pathname in compiled.pathRewrites) {
        return { kind: 'rewrite', newPath: compiled.pathRewrites[pathname] };
    }
    const hit = compiled.matchers.find((m) => m.regexp.test(pathname));
    if (hit) return { kind: 'native' };
    return { kind: 'legacy' };
}

/** 通知 shell 切换路由（由 iframe 侧 beforeEach 发起，用于 native/rewrite 场景） */
export function notifyShellNavigate(path: string): void {
    try {
        window.parent.postMessage(
            { v: 1, type: 'iframe:route-change', source: 'iframe', payload: { path } },
            window.location.origin,
        );
    } catch (e) {
        console.error('[embedded-bridge] notify shell navigate failed:', e);
    }
}

function isValidMessage(event: MessageEvent): event is MessageEvent<MessageEnvelope> {
    if (event.origin !== window.location.origin) return false;
    const data = event.data;
    if (!data || typeof data !== 'object') return false;
    if (data.v !== 1) return false;
    if (data.source !== 'shell') return false;
    if (typeof data.type !== 'string') return false;
    return true;
}

function sendToShell(type: string, payload?: any) {
    try {
        window.parent.postMessage(
            { v: 1, type, source: 'iframe', payload },
            window.location.origin,
        );
    } catch (e) {
        console.error('[embedded-bridge] failed to send:', type, e);
    }
}

/** 通知新版壳展示套餐/功能限制引导弹窗 */
export function notifyShellPackageGuidePrompt(payload: PackageGuidePromptPayload): boolean {
    if (!isShellEmbedded()) return false;

    sendToShell('iframe:package-guide-prompt', payload);
    return true;
}

/** 通知新版壳展示 AI 机器人升级为 AI 成员的引导弹窗 */
export function notifyShellAiRobotUpgradeGuide(payload?: AiRobotUpgradeGuidePayload): boolean {
    if (!isShellEmbedded()) return false;

    sendToShell('iframe:ai-robot-upgrade-guide', payload);
    return true;
}

/** 通知新版壳切换为原生新版 Chat，避免 iframe 内部硬跳 `/next` 造成新旧版嵌套。 */
export function notifyShellSwitchNewUi(payload: SwitchNewUiPayload): boolean {
    if (!isShellEmbedded()) return false;

    sendToShell('iframe:switch-new-ui', payload);
    return true;
}

/**
 * 全局 click 委托：嵌入模式下拦截 iframe 内部 `<a href="/...">` 内部链接，
 * 改由新版壳通过 router.push 完成 SPA 切换。
 *
 * 背景：errorCode.ts / feeLimit / packageLimit 等 i18n 文案大量使用 `<a href="/fee">`
 * 这类绝对路径锚点；iframe 默认行为会让自身整页跳转到目标路径，shell URL 不变，
 * 导致布局错乱或落入旧版 bootstrap 链路。把这类点击 preventDefault 后转交 shell，
 * 是覆盖所有"i18n 文案内置跳转"入口的最低成本方案，不必逐条改文案。
 *
 * 拦截边界：
 * - 仅左键、无修饰键（用户用 Ctrl/Cmd/中键想新开标签时按浏览器原生行为）
 * - href 必须是站内绝对路径（以 `/` 起、且不是 `//`）
 * - target=_blank、download、协议链接（http/https/mailto/tel）以及 `#` 锚点不拦
 */
function shouldInterceptAnchorClick(event: MouseEvent, anchor: HTMLAnchorElement): string | null {
    if (event.defaultPrevented) return null;
    if (event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return null;
    if (anchor.hasAttribute('download')) return null;

    const rawHref = anchor.getAttribute('href');
    if (!rawHref) return null;
    if (rawHref.startsWith('#')) return null;
    if (rawHref.startsWith('//')) return null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(rawHref)) return null;
    if (!rawHref.startsWith('/')) return null;

    return rawHref;
}

function attachInternalAnchorInterceptor(): void {
    document.addEventListener(
        'click',
        (event) => {
            const target = event.target as Element | null;
            if (!target) return;
            const anchor = target.closest('a') as HTMLAnchorElement | null;
            if (!anchor) return;

            const path = shouldInterceptAnchorClick(event, anchor);
            if (!path) return;

            event.preventDefault();
            sendToShell('iframe:navigate-request', { path });
        },
        true,
    );
}

export default function initEmbeddedBridge(router: Router) {
    if (!isShellEmbedded()) return;

    // 1. 监听 shell 消息（先移除旧监听器，避免重复调用时监听器堆叠）
    if (activeMessageHandler) {
        window.removeEventListener('message', activeMessageHandler);
    }
    activeMessageHandler = (event: MessageEvent) => {
        if (!isValidMessage(event)) return;
        const { type, payload } = event.data;

        switch (type) {
            case 'shell:navigate': {
                if (payload && typeof payload.path === 'string') {
                    shellDrivenNavigation = true;
                    router.replace(payload.path)
                        .catch((err: any) => {
                            // NavigationDuplicated 和 NavigationRedirected 均为 vue-router 3 已知的"正常"异常
                            if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationRedirected') {
                                console.error('[embedded-bridge] navigate failed:', err);
                            }
                        })
                        .finally(() => {
                            shellDrivenNavigation = false;
                        });
                }
                break;
            }
            case 'shell:logout': {
                try {
                    window.dispatchEvent(new CustomEvent('embedded:logout'));
                } catch (e) {
                    console.error('[embedded-bridge] logout cleanup failed:', e);
                }
                break;
            }
            case 'shell:routes-sync': {
                if (
                    payload &&
                    Array.isArray(payload.nativePatterns) &&
                    payload.pathRewrites &&
                    typeof payload.pathRewrites === 'object'
                ) {
                    applyRegistry({
                        nativePatterns: payload.nativePatterns,
                        pathRewrites: payload.pathRewrites,
                    });
                }
                break;
            }
            case 'shell:navigate-guard': {
                if (!payload || typeof payload.id !== 'string' || typeof payload.path !== 'string') {
                    break;
                }
                const { id, path, force } = payload as { id: string; path: string; force?: boolean };

                // 立刻发 ack，shell 取消 1.5s 协议级超时
                sendToShell('iframe:navigate-guard-ack', { id });

                if (force === true) {
                    // 强制跳转通道：跳过守卫，直接 router.replace
                    shellDrivenNavigation = true;
                    router.replace(path)
                        .catch((err: any) => {
                            if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationRedirected') {
                                // shell 已收到 allow=true，此处失败会造成 shell/iframe 路径不一致；
                                // force 通道按 spec §5.1 设计为 fire-and-forget，记录日志便于排查
                                console.error('[embedded-bridge] force navigate failed (shell already received allow=true):', err);
                            }
                        })
                        .finally(() => { shellDrivenNavigation = false; });
                    sendToShell('iframe:navigate-guard-result', { id, allow: true });
                    break;
                }

                // 新批次起点：上一批次完全清空（inflight=null）后第一个新请求
                // 复位 guardCancelled，让本批次重新评估"用户是否要 leave"
                if (!inflightGuard) {
                    guardCancelled = false;
                }

                // 旧 inflight 被新请求覆盖：立刻回 false
                if (inflightGuard) {
                    sendToShell('iframe:navigate-guard-result', { id: inflightGuard.id, allow: false });
                    inflightGuard = null;
                }
                inflightGuard = { id };

                // 串行排队：等上一个 tryGuardedNavigate 完全 settle 才开始
                guardChain = guardChain.then(async () => {
                    if (inflightGuard?.id !== id) return; // 排队期间又被新请求覆盖，本次作废

                    // 同批次内之前 step 已被用户取消，本 step 不再触发 router.push
                    // 避免 dirty 页 beforeRouteLeave 二次弹 Modal（闪烁 + iView Modal 状态错乱）
                    if (guardCancelled) {
                        if (inflightGuard?.id === id) {
                            inflightGuard = null;
                            sendToShell('iframe:navigate-guard-result', { id, allow: false });
                        }
                        return;
                    }

                    const allow = await tryGuardedNavigate(router, path);
                    if (!allow) {
                        // 本 step 被用户取消，标记本批次内后续 step 全部短路
                        guardCancelled = true;
                    }
                    if (inflightGuard?.id !== id) return;
                    inflightGuard = null;
                    sendToShell('iframe:navigate-guard-result', { id, allow });
                }).catch((err) => {
                    // 兜底：tryGuardedNavigate 契约上恒 resolve，但避免未来回归导致整条链卡死
                    console.error('[embedded-bridge] guard chain step failed, releasing inflight:', err);
                    if (inflightGuard?.id === id) {
                        inflightGuard = null;
                        sendToShell('iframe:navigate-guard-result', { id, allow: false });
                    }
                });
                break;
            }
            case 'shell:ping': {
                /**
                 * shell 在加载 slow 阶段(30-60s)发起的心跳探活。
                 * 这里只回 pong,不依赖 router/store/i18n 任何业务模块完成初始化 —
                 * 只要本 message handler 已经注册(initEmbeddedBridge 已跑),pong 就能发出去,
                 * 用于救回 iframe:ready 因为时序/弱网/异常被错过的场景。
                 * seq 由 shell 递增,仅作诊断回填,不参与判定。
                 */
                const seq = (payload && typeof payload.seq === 'number') ? payload.seq : 0;
                sendToShell('iframe:pong', { seq });
                break;
            }
            default:
                break;
        }
    };
    window.addEventListener('message', activeMessageHandler);

    // 2. 监听 document.title 变化
    let lastTitle = document.title;
    if (lastTitle) {
        sendToShell('iframe:title-change', { title: lastTitle });
    }

    const titleEl = document.querySelector('title');
    if (titleEl) {
        const observer = new MutationObserver(() => {
            const newTitle = document.title;
            if (newTitle !== lastTitle) {
                lastTitle = newTitle;
                sendToShell('iframe:title-change', { title: newTitle });
            }
        });
        observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }

    // 3. 拦截 iframe 内部 `<a href="/...">` 点击，转发给 shell 由 router.push 处理
    attachInternalAnchorInterceptor();

    console.log('[embedded-bridge] initialized');
}
